import { Box3, Vector2, Vector2Tuple, Vector3 } from 'three';

import { largestRect, pointRotate } from '@spazious/dollhouse-three';

import {
  Schema,
  SchemaFieldFurniture,
  SchemaFieldNumber,
  SchemaFieldTypesMapping,
  SchemaValues,
} from '../schema/index.js';
import { FInstanceNode } from '../types/index.js';

import {
  FArrangementBase,
  FArrangementBaseSchema,
  FArrangementBaseSchemaFields,
} from './FArrangementBase.js';

const bbox = new Box3();
const vec3 = new Vector3();

export interface FArrangementBoardroomSchemaFields extends FArrangementBaseSchemaFields {
  chairType: SchemaFieldFurniture;
  tableType: SchemaFieldFurniture;
  noOfChairs: SchemaFieldNumber;
  chairsGap: SchemaFieldNumber;
  chairDistanceFromTable: SchemaFieldNumber;
}

export interface FArrangementBoardroomSchema extends FArrangementBaseSchema {
  fields: FArrangementBoardroomSchemaFields;
}

export interface FArrangementBoardroomValues {
  tableSize: SchemaFieldTypesMapping['vec2'];
  chairSize: SchemaFieldTypesMapping['vec2'];
  chairDistanceFromTable: SchemaFieldTypesMapping['number'];
}

export type FArrangementBoardroomAttributes = Partial<
  SchemaValues<FArrangementBoardroomSchemaFields>
> &
  FArrangementBoardroomValues;

export class FArrangementBoardroom<
  TSchema extends FArrangementBoardroomSchema = FArrangementBoardroomSchema,
  TValues extends Record<string, any> = {},
  TFArrangementSchema extends Schema = Schema,
  TFArrangementValues extends Record<string, any> = {}
> extends FArrangementBase<
  TSchema,
  TValues & FArrangementBoardroomValues,
  TFArrangementSchema,
  TFArrangementValues
> {
  constructor(
    schema: TSchema,
    initialValues?: FArrangementBoardroomAttributes & TValues & TFArrangementValues
  ) {
    super(schema, {
      maxElements: Infinity,
      elementSize: [0, 0],
      size: [4, 4],
      tableSize: [0, 0],
      chairSize: [0, 0],
      ...(initialValues as any),
    });
    this.setValue('getMaxChairs', this.getMaxChairs);
  }

  getNodeTypes(): number[] {
    const { chairType, tableType } =
      this.getValues() as SchemaValues<FArrangementBoardroomSchemaFields> &
        FArrangementBoardroomValues;

    if (!chairType) {
      throw new Error('chairType is required');
    }

    if (!tableType) {
      throw new Error('tableType is required');
    }

    return [chairType, tableType];
  }

  updateObjectMeasurements() {
    const [chair, table] = this.getObjects();

    if (!chair) {
      throw new Error('chair table object');
    }

    if (!table) {
      throw new Error('missing table object');
    }

    const tableBoundingBox = bbox.setFromObject(table);
    tableBoundingBox.getSize(vec3);
    const tableSize = new Vector2(vec3.x, vec3.z);

    const chairBoundingBox = bbox.setFromObject(chair);
    chairBoundingBox.getSize(vec3);
    const chairSize = new Vector2(vec3.x, vec3.z);
    const elementSize = [
      tableSize.x + 2 * chairSize.y,
      2 * chairSize.y + 2 * tableSize.y + tableSize.x,
    ];
    this.updateValues({
      tableSize: tableSize.toArray(),
      chairSize: chairSize.toArray(),
      elementSize,
    } as any);
  }

  getMinimumSize(): Vector2Tuple {
    const { tableSize, chairSize, chairDistanceFromTable } = this.getValues();

    return [
      2 *
        (tableSize[1] +
          chairDistanceFromTable * chairSize[1] * 0.5 +
          chairSize[1] * 0.5 +
          Number.EPSILON),
      tableSize[0] + Number.EPSILON,
    ];
  }

  getMaxChairs = () => {
    const {
      size: boundingBoxSize,
      tableSize,
      chairSize,
      chairsGap,
      chairDistanceFromTable,
      mask,
    } = this.getValues() as SchemaValues<FArrangementBoardroomSchemaFields> &
      FArrangementBoardroomValues;

    let size = boundingBoxSize;
    if (mask?.length) {
      const result = largestRect(mask[0]);
      if (result.maxRect) {
        size = [result.maxRect.width, result.maxRect.height];
      }
    }

    const { noOfChairsInFrontRow, noOfChairsInBackRow, noOfChairsInLeftRow, noOfChairsInRightRow } =
      this.getChairAndTableCounts({
        size,
        chairSize,
        tableSize,
        chairDistanceFromTable,
        chairsGap,
        noOfChairs: Infinity,
      });

    const totalChairs =
      noOfChairsInFrontRow + noOfChairsInBackRow + noOfChairsInLeftRow + noOfChairsInRightRow;
    return totalChairs;
  };

  /**
   * Tables are always placed in the following layout(placed in clockwise direction):
   * - Tables aligned to X-axis (front and back) go from left edge to right edge
   * - Tables aligned to Z-axis (left and right) are wedged between front and back tables
   * ----------------------
   * |   FL   |           |
   * ----------------------
   * |    |          |    |
   * |    |          | FR |
   * |    |          |    |
   * | _  |          |  _ |
   * |    |          |    |
   * |    |          |    |
   * | BL |          |    |
   * |    |          |    |
   * |    |          |    |
   * ----------------------
   * |        |    BR     |
   * ----------------------
   * - In case there is not enough space, only two tables will be placed side by side in either X or Z direction
   *    _     _
   * |    ||    |
   * | BL || FR |
   * |    ||    |
   * | _  ||  _ |
   *
   *  OR
   *
   * ----------
   * |   FL   |
   * ----------
   * ----------
   * |   BR   |
   * ----------
   *
   * Chair positions are calculated in clockwise direction too
   */
  getChairAndTableCounts({
    size,
    chairSize,
    tableSize,
    chairDistanceFromTable,
    chairsGap,
    noOfChairs,
  }: {
    size: Vector2Tuple;
    chairSize: Vector2Tuple;
    tableSize: Vector2Tuple;
    chairDistanceFromTable: number;
    chairsGap: number;
    noOfChairs: number;
  }) {
    const [sizeX, sizeY] = size;
    const chairRadius = chairSize[1] / 2;
    // distance of chair front-edge from center of table
    const distanceFromCenter = chairDistanceFromTable * chairRadius - chairRadius; // chairDistanceFromTable is 0, chair center is at the edge of the table
    const itemSize = chairSize[0] + chairsGap;
    const noOfChairsPerTable = tableSize[0] / itemSize;

    const elementSize = [tableSize[0], tableSize[1] + distanceFromCenter + chairSize[1]];

    const maxTablesInZAxis =
      sizeX >= 2 * elementSize[1] // ensure X size can fit two tables and its chairs
        ? sizeY >= elementSize[0] + 2 * elementSize[1]
          ? Math.floor((sizeY - 2 * elementSize[1]) / elementSize[0])
          : Math.floor(sizeY / elementSize[0])
        : 0;
    const maxTablesInXAxis =
      sizeY >= elementSize[0] + 2 * elementSize[1] // ensure Y size can fit one vertical tables and 2 sets of horizontal tables
        ? Math.floor((sizeX - 2 * (chairSize[1] + distanceFromCenter)) / elementSize[0])
        : 0;

    let maxChairsOnZAxisRow = Math.floor(maxTablesInZAxis * noOfChairsPerTable);
    if (!maxChairsOnZAxisRow && maxTablesInZAxis > 0) {
      maxChairsOnZAxisRow = 1;
    }

    let maxChairsOnXAxisRow = Math.floor(maxTablesInXAxis * noOfChairsPerTable);
    if (!maxChairsOnXAxisRow && maxTablesInXAxis > 0) {
      maxChairsOnXAxisRow = 1;
    }

    const maxChairs = 2 * (maxChairsOnZAxisRow + maxChairsOnXAxisRow);
    const chairCount = this.getSafeIterations(Math.min(noOfChairs, maxChairs));

    const numberOfSides = maxTablesInZAxis === 0 || maxTablesInXAxis === 0 ? 2 : 4;

    const idealNoOfPeopleOnEachSide = Math.ceil(chairCount / numberOfSides);

    const isZAxisLonger = maxTablesInZAxis > maxTablesInXAxis;
    const [maxNoOfTablesOnLongSide, maxNoOfTablesOnShortSide] = isZAxisLonger
      ? [maxTablesInZAxis, maxTablesInXAxis]
      : [maxTablesInXAxis, maxTablesInZAxis];

    let noOfTablesNeededOnShorterSide = Math.min(
      Math.ceil(idealNoOfPeopleOnEachSide / noOfChairsPerTable),
      maxNoOfTablesOnShortSide
    );

    let noOfPeopleOnEachShorterSide = Math.min(
      idealNoOfPeopleOnEachSide,
      noOfTablesNeededOnShorterSide > 0
        ? Math.max(1, Math.floor(noOfTablesNeededOnShorterSide * noOfChairsPerTable))
        : 0
    );
    let noOfPeopleSeatedOnShorterSide = 2 * noOfPeopleOnEachShorterSide;
    let minNoOfPeopleSeatedOnLongerSide = chairCount - noOfPeopleSeatedOnShorterSide;
    let noOfTablesNeededOnLongerSide = Math.min(
      Math.ceil(Math.ceil(minNoOfPeopleSeatedOnLongerSide / 2) / noOfChairsPerTable),
      maxNoOfTablesOnLongSide
    );

    // re-adjust max chair count based on needed number of tables
    let maxChairsOnShorterSide = Math.floor(noOfTablesNeededOnShorterSide * noOfChairsPerTable);
    if (maxChairsOnShorterSide === 0 && noOfTablesNeededOnShorterSide > 0) {
      maxChairsOnShorterSide = 1;
    }
    let maxChairsOnLongerSide = Math.floor(noOfTablesNeededOnLongerSide * noOfChairsPerTable);
    if (maxChairsOnLongerSide === 0 && noOfTablesNeededOnLongerSide > 0) {
      maxChairsOnLongerSide = 1;
    }

    let chairsOnXAxisRows, chairsOnZAxisRows;
    let noOfTablesNeededOnXAxis, noOfTablesNeededOnZAxis;
    if (isZAxisLonger) {
      maxChairsOnZAxisRow = maxChairsOnLongerSide;
      maxChairsOnXAxisRow = maxChairsOnShorterSide;

      chairsOnXAxisRows = Math.min(noOfPeopleSeatedOnShorterSide, maxChairsOnShorterSide * 2);
      chairsOnZAxisRows = Math.min(
        chairCount - noOfPeopleSeatedOnShorterSide,
        maxChairsOnLongerSide * 2
      );

      noOfTablesNeededOnXAxis = noOfTablesNeededOnShorterSide;
      noOfTablesNeededOnZAxis = noOfTablesNeededOnLongerSide;
    } else {
      maxChairsOnZAxisRow = maxChairsOnShorterSide;
      maxChairsOnXAxisRow = maxChairsOnLongerSide;

      chairsOnZAxisRows = Math.min(noOfPeopleSeatedOnShorterSide, maxChairsOnShorterSide * 2);
      chairsOnXAxisRows = Math.min(
        chairCount - noOfPeopleSeatedOnShorterSide,
        maxChairsOnLongerSide * 2
      );

      noOfTablesNeededOnZAxis = noOfTablesNeededOnShorterSide;
      noOfTablesNeededOnXAxis = noOfTablesNeededOnLongerSide;
    }

    const noOfChairsInFrontRow = Math.min(
      Math.floor(chairsOnXAxisRows / 2), // already calculated (minimum needed)
      maxChairsOnXAxisRow // possible based on table count
    );
    const noOfChairsInBackRow = Math.min(
      chairsOnXAxisRows - noOfChairsInFrontRow,
      maxChairsOnXAxisRow
    );

    const noOfChairsInLeftRow = Math.min(
      Math.floor(chairsOnZAxisRows / 2), // already calculated (minimum needed)
      maxChairsOnZAxisRow // possible based on table count
    );
    const noOfChairsInRightRow = Math.min(
      chairsOnZAxisRows - noOfChairsInLeftRow,
      maxChairsOnZAxisRow
    );

    return {
      maxChairs,
      noOfTablesNeededOnZAxis,
      noOfTablesNeededOnXAxis,
      noOfChairsInFrontRow,
      noOfChairsInBackRow,
      noOfChairsInLeftRow,
      noOfChairsInRightRow,
      itemSize,
      distanceFromCenter,
      sizeX,
      sizeY,
      chairDistanceFromTable,
    };
  }

  computeNodes(): FInstanceNode[] {
    if (this.getObjects().length === 0) {
      return [];
    }

    const {
      chairType,
      tableType,
      noOfChairs,
      chairsGap,
      chairDistanceFromTable,
      tableSize,
      chairSize,
      size: boundingBoxSize,
      mask,
    } = this.getValues() as SchemaValues<FArrangementBoardroomSchemaFields> &
      FArrangementBoardroomValues;

    let size = boundingBoxSize;
    let center: Vector2Tuple = [0, 0]; // center of the rectangle
    let alpha = 0; // rectangle orientation angle in radians
    if (mask?.length) {
      const result = largestRect(mask[0]);
      if (result.maxRect) {
        size = [result.maxRect.width, result.maxRect.height];
        center = [result.maxRect.cx, result.maxRect.cy];
        alpha = result.maxRect.angle * (Math.PI / 180);
      }
    }

    const nodes: FInstanceNode[] = [];

    const {
      maxChairs,
      noOfTablesNeededOnZAxis,
      noOfTablesNeededOnXAxis,
      noOfChairsInFrontRow,
      noOfChairsInBackRow,
      noOfChairsInLeftRow,
      noOfChairsInRightRow,
      itemSize,
      distanceFromCenter,
    } = this.getChairAndTableCounts({
      size,
      chairSize,
      tableSize,
      chairDistanceFromTable,
      chairsGap,
      noOfChairs,
    });

    let chairCount = this.getSafeIterations(Math.min(noOfChairs, maxChairs));

    let padding = 0;
    if (noOfTablesNeededOnXAxis === 0) {
      padding = tableSize[1];
    }

    // Initial table positions
    let FRtPos = [
      (noOfTablesNeededOnXAxis * tableSize[0] - tableSize[1]) / 2 + padding,
      -(noOfTablesNeededOnZAxis * tableSize[0] - tableSize[0]) / 2,
    ] as Vector2Tuple;

    let BRtPos = [
      (noOfTablesNeededOnXAxis * tableSize[0] - tableSize[0]) / 2,
      (noOfTablesNeededOnZAxis * tableSize[0] + tableSize[1]) / 2,
    ] as Vector2Tuple;

    let BLtPos = [-FRtPos[0], -FRtPos[1]] as Vector2Tuple;
    let FLtPos = [-BRtPos[0], -BRtPos[1]] as Vector2Tuple;

    // Initial chair positions
    let FRcPos = [
      FRtPos[0] + tableSize[1] / 2 + distanceFromCenter + chairSize[1] / 2,
      -((noOfChairsInRightRow * itemSize) / 2) + itemSize / 2,
    ] as Vector2Tuple;
    let BRcPos = [
      (noOfChairsInBackRow * itemSize - itemSize) / 2,
      BRtPos[1] + tableSize[1] / 2 + distanceFromCenter + chairSize[1] / 2,
    ] as Vector2Tuple;
    let BLcPos = [
      BLtPos[0] - tableSize[1] / 2 - distanceFromCenter - chairSize[1] / 2,
      (noOfChairsInLeftRow * itemSize - itemSize) / 2,
    ] as Vector2Tuple;
    let FLcPos = [
      -((noOfChairsInFrontRow * itemSize - itemSize) / 2),
      FLtPos[1] - tableSize[1] / 2 - distanceFromCenter - chairSize[1] / 2,
    ] as Vector2Tuple;

    let chairCounter = 0;

    // Add FrontLeft Tables(A)
    for (let i = 0; i < this.getSafeIterations(noOfTablesNeededOnXAxis); i++) {
      let tablePosition = [...FLtPos] as Vector2Tuple;
      tablePosition[0] += i * tableSize[0];
      nodes.push({
        type: 'table',
        id: tableType,
        position: pointRotate(
          [tablePosition[0] + center[0], tablePosition[1] + center[1]],
          alpha,
          center
        ),
        rotation: -Math.PI - alpha,
      });
    }

    // Add FrontLeft Chairs(A)
    for (
      let i = 0;
      i < this.getSafeIterations(noOfChairsInFrontRow) && chairCounter < chairCount;
      i++
    ) {
      let chairPosition = [...FLcPos] as Vector2Tuple;
      chairPosition[0] += i * (chairSize[0] + chairsGap);
      nodes.push({
        type: 'chair',
        id: chairType,
        position: pointRotate(
          [chairPosition[0] + center[0], chairPosition[1] + center[1]],
          alpha,
          center
        ),
        rotation: -Math.PI - alpha,
      });
      chairCounter += 1;
    }

    // Add BackRight Tables(B)
    for (let i = 0; i < this.getSafeIterations(noOfTablesNeededOnXAxis); i++) {
      let tablePosition = [...BRtPos] as Vector2Tuple;
      tablePosition[0] -= i * tableSize[0];
      nodes.push({
        type: 'table',
        id: tableType,
        position: pointRotate(
          [tablePosition[0] + center[0], tablePosition[1] + center[1]],
          alpha,
          center
        ),
        rotation: 0 - alpha,
      });
    }

    // Add BackRight Chairs(B)
    for (
      let i = 0;
      i < this.getSafeIterations(noOfChairsInBackRow) && chairCounter < chairCount;
      i++
    ) {
      let chairPosition = [...BRcPos] as Vector2Tuple;
      chairPosition[0] -= i * (chairSize[0] + chairsGap);
      nodes.push({
        type: 'chair',
        id: chairType,
        position: pointRotate(
          [chairPosition[0] + center[0], chairPosition[1] + center[1]],
          alpha,
          center
        ),
        rotation: 0 - alpha,
      });
      chairCounter += 1;
    }

    // Add BackLeft Tables(C)
    for (let i = 0; i < this.getSafeIterations(noOfTablesNeededOnZAxis); i++) {
      let tablePosition = [...BLtPos] as Vector2Tuple;
      tablePosition[1] -= i * tableSize[0];
      nodes.push({
        type: 'table',
        id: tableType,
        position: pointRotate(
          [tablePosition[0] + center[0], tablePosition[1] + center[1]],
          alpha,
          center
        ),
        rotation: -Math.PI / 2 - alpha,
      });
    }

    // Add BackLeft Chairs(C)
    for (
      let i = 0;
      i < this.getSafeIterations(noOfChairsInLeftRow) && chairCounter < chairCount;
      i++
    ) {
      let chairPosition = [...BLcPos] as Vector2Tuple;
      chairPosition[1] -= i * (chairSize[0] + chairsGap);
      nodes.push({
        type: 'chair',
        id: chairType,
        position: pointRotate(
          [chairPosition[0] + center[0], chairPosition[1] + center[1]],
          alpha,
          center
        ),
        rotation: -Math.PI / 2 - alpha,
      });
      chairCounter += 1;
    }

    // Add FrontRight Tables(D)
    for (let i = 0; i < this.getSafeIterations(noOfTablesNeededOnZAxis); i++) {
      let tablePosition = [...FRtPos] as Vector2Tuple;
      tablePosition[1] += i * tableSize[0];
      nodes.push({
        type: 'table',
        id: tableType,
        position: pointRotate(
          [tablePosition[0] + center[0], tablePosition[1] + center[1]],
          alpha,
          center
        ),
        rotation: Math.PI / 2 - alpha,
      });
    }

    // Add FrontRight Chairs(D)
    for (
      let i = 0;
      i < this.getSafeIterations(noOfChairsInRightRow) && chairCounter < chairCount;
      i++
    ) {
      let chairPosition = [...FRcPos] as Vector2Tuple;
      chairPosition[1] += i * (chairSize[0] + chairsGap);
      nodes.push({
        type: 'chair',
        id: chairType,
        position: pointRotate(
          [chairPosition[0] + center[0], chairPosition[1] + center[1]],
          alpha,
          center
        ),
        rotation: Math.PI / 2 - alpha,
      });
      chairCounter += 1;
    }

    return nodes.filter((node) => this.isNodeWithinNavmesh(node));
  }
}
